import { APP_VERSION } from '../version';
import { reducers } from './root-reducer';

const STATE_VERSION_KEY = 'drr_state_version';
const SESSION_MARKER_KEY = 'drr_session_active';

/**
 * List of reducer keys that are stored in sessionStorage.
 * This should match the keys from root-reducer.ts minus the blacklist from sessionStorage.meta.ts
 */
const getSessionStorageKeys = (): string[] => {
  const blackList = ['forms', 'loading', 'router'];
  return Object.keys(reducers).filter((feature) => blackList.indexOf(feature) === -1);
};

/**
 * Checks if there's any DRR state in storage
 */
const hasExistingState = (): boolean => {
  try {
    const keys = getSessionStorageKeys();
    return keys.some((key) => window.sessionStorage?.getItem(key) !== null);
  } catch {
    return false;
  }
};

/**
 * Gets the stored session marker
 */
const getSessionMarker = (): string | null => {
  try {
    return window.sessionStorage?.getItem(SESSION_MARKER_KEY) ?? null;
  } catch {
    return null;
  }
};

/**
 * Sets the session marker (indicates this version of the library is managing state)
 */
const setSessionMarker = (): void => {
  try {
    // Use the version as the marker so we know which version "owns" this session
    window.sessionStorage?.setItem(SESSION_MARKER_KEY, APP_VERSION);
  } catch {
    // Silently fail
  }
};

/**
 * Removes the session marker
 */
const removeSessionMarker = (): void => {
  try {
    window.sessionStorage?.removeItem(SESSION_MARKER_KEY);
  } catch {
    // Silently fail
  }
};

/**
 * Initialize the library session.
 * This should be called BEFORE the store rehydrates from storage.
 *
 * Logic:
 * 1. If session marker exists AND matches current version → page refresh, keep state
 * 2. If session marker exists but version doesn't match → version change, clear state
 * 3. If state exists but NO session marker → orphaned state from old version, clear state
 * 4. If no state → fresh start
 */
export const initializeSession = (): void => {
  try {
    const storedVersion = getStoredVersion();
    const sessionMarker = getSessionMarker();
    const existingState = hasExistingState();

    let shouldClear = false;
    let reason = '';

    if (sessionMarker !== null) {
      // Session marker exists - a version with this code was running before
      if (sessionMarker !== APP_VERSION || storedVersion !== APP_VERSION) {
        // Version mismatch - different library version loaded
        shouldClear = true;
        reason = `version mismatch (marker: ${sessionMarker}, stored: ${storedVersion}, current: ${APP_VERSION})`;
      }
      // else: Same version, this is a page refresh - keep state
    } else if (existingState) {
      // State exists but NO session marker
      // This means an old version (without session tracking) left state behind
      shouldClear = true;
      reason = 'orphaned state from old version (no session marker)';
    }
    // else: No marker and no state = fresh start, nothing to clear

    if (shouldClear) {
      console.log(`[DRR] Clearing stale state. Reason: ${reason}`);
      clearAllPersistedState();
    }

    // Mark our session as active with the current version
    setSessionMarker();
    setStoredVersion(APP_VERSION);

    console.log(`[DRR] Session initialized. Version: ${APP_VERSION}, State preserved: ${!shouldClear}`);
  } catch (error) {
    console.warn('[DRR] Error initializing session:', error);
  }
};

/**
 * Destroy the library session.
 * This should be called when the library/component is destroyed (e.g., native app switches away).
 * Clears all persisted state to prevent stale state issues on next load.
 */
export const destroySession = (): void => {
  try {
    console.log('[DRR] Destroying session and clearing state');
    clearAllPersistedState();
    removeSessionMarker();
    removeStoredVersion();
  } catch (error) {
    console.warn('[DRR] Error destroying session:', error);
  }
};

/**
 * Clears all persisted state from sessionStorage.
 */
export const clearAllPersistedState = (): void => {
  clearSessionStorage();
};

/**
 * Clears sessionStorage keys used by the DRR state.
 */
const clearSessionStorage = (): void => {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  try {
    const keysToRemove = getSessionStorageKeys();
    keysToRemove.forEach((key) => {
      window.sessionStorage.removeItem(key);
    });
    console.log(`[DRR] Cleared ${keysToRemove.length} sessionStorage keys`);
  } catch (error) {
    console.warn('[DRR] Failed to clear sessionStorage:', error);
  }
};

/**
 * Gets the stored state version from sessionStorage.
 */
const getStoredVersion = (): string | null => {
  try {
    return window.sessionStorage?.getItem(STATE_VERSION_KEY) ?? null;
  } catch {
    return null;
  }
};

/**
 * Stores the current state version in sessionStorage.
 */
const setStoredVersion = (version: string): void => {
  try {
    window.sessionStorage?.setItem(STATE_VERSION_KEY, version);
  } catch {
    // Silently fail in environments without storage
  }
};

/**
 * Removes the stored state version from sessionStorage.
 */
export const removeStoredVersion = (): void => {
  try {
    window.sessionStorage?.removeItem(STATE_VERSION_KEY);
  } catch {
    // Silently fail
  }
};
